<template>
  <div>
    <LoadingToast
      v-model="isActivatorLoading"
      title="author.publishInActivator.opening"
      subtitle="author.publishInActivator.mayTakeAWhile"
      :progress="activatorProgress"
    />
    <LoadingToast
      v-model="isActivatorError"
      title="author.publishInActivator.wentWrong"
      subtitle="author.publishInActivator.tryAgain"
      :progress="0"
      :on-close="() => (isActivatorError = false)"
    />
    <LoadingToast
      v-model="isEwizardPublishing"
      title="author.publishInEwizard.publishing"
      subtitle="author.publishInEwizard.mayTakeAWhile"
      :progress="ewizardProgress"
    />
    <LoadingToast
      v-model="isEwizardError"
      title="author.publishInEwizard.wentWrong"
      subtitle="author.publishInEwizard.tryAgain"
      :progress="0"
      :on-close="() => (isEwizardError = false)"
    />

    <BbBtn
      v-if="primaryConnector"
      v-bind="{ type: 'primary' }"
      class="dropdown-button"
      size="small"
      @click="primaryConnector.publish"
    >
      <BbIcon size="small" :name="primaryConnector.icon" :color="primaryConnector.color" />
      {{ primaryConnector.label }}
      <template #menu>
        <BbMenuItem
          v-for="connector in menuConnectors"
          :key="connector.key"
          :is-disabled="connector.isBusy"
          @click="connector.publish"
        >
          <BbIcon size="small" :name="connector.isBusy ? 'spin' : connector.icon" :color="connector.isBusy ? undefined : connector.color" />
          <TextBody :text="connector.label" tag="span" />
        </BbMenuItem>
        <BbMenuItem :is-disabled="isDownloadInProgress" @click="$emit('download')">
          <BbIcon size="small" :name="isDownloadInProgress ? 'spin' : 'download'" />
          <TextBody i18n-key="common.download" tag="span" />
        </BbMenuItem>
      </template>
    </BbBtn>
  </div>
</template>

<script>
import { ref, computed } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { v4 as uuidv4 } from 'uuid';

import { IsDownloadInProgress } from '~/constants/getters';
import { AddToastNotification } from '~/constants/mutations';

import TextBody from '~/components/common/TextBody.vue';
import LoadingToast from '~/components/common/LoadingToast.vue';
import useAuthoring from '~/composables/useAuthoring';

export default {
  components: { TextBody, LoadingToast },
  props: {
    connectorAccess: {
      type: Object,
      default: () => ({}),
    },
  },
  emits: ['download'],
  setup(props) {
    const route = useRoute();
    const store = useStore();
    const { t } = useI18n();
    const { getActivatorUrl, publishToEwizard } = useAuthoring();

    const isDownloadInProgress = computed(() =>
      store.getters[`documentStore/${IsDownloadInProgress}`](route.params.conversationId),
    );
    const isActivatorLoading = ref(false);
    const activatorProgress = ref(0);
    const isActivatorError = ref(false);

    const isEwizardPublishing = ref(false);
    const ewizardProgress = ref(0);
    const isEwizardError = ref(false);

    const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

    const stepProgress = async (target) => {
      for (const [value, wait] of [
        [0, 200],
        [25, 400],
        [59, 600],
        [75, 0],
      ]) {
        target.value = value;
        if (wait) await delay(wait);
      }
    };

    const withProgress = async (isBusy, progress, task) => {
      isBusy.value = true;
      progress.value = 0;
      await stepProgress(progress);

      try {
        await task();
      } finally {
        await delay(200);
        isBusy.value = false;
        progress.value = 0;
      }
    };

    const publishInActivator = () =>
      withProgress(isActivatorLoading, activatorProgress, async () => {
        const { url, statusCode } = await getActivatorUrl(route.params.conversationId);
        if (statusCode === 404) {
          isActivatorError.value = true;
        } else if (url) {
          activatorProgress.value = 100;
          await delay(300);
          window.open(url, '_blank');
        }
      });

    const publishInEwizard = () => {
      if (isEwizardPublishing.value) return;

      return withProgress(isEwizardPublishing, ewizardProgress, async () => {
        const { success } = await publishToEwizard(route.params.conversationId);
        if (success) {
          ewizardProgress.value = 100;
          await delay(300);
          store.commit(`notificationStore/${AddToastNotification}`, {
            type: 'toast',
            title: t('author.publishInEwizard.queued'),
            notification: { externalId: uuidv4() },
            createdAt: Date.now(),
          });
        } else {
          isEwizardError.value = true;
        }
      });
    };

    const connectors = computed(() => [
      {
        key: 'activator',
        label: t('author.publishInActivator.title'),
        icon: 'act',
        color: '#F242FF',
        hasAccess: props.connectorAccess.activator,
        isBusy: isActivatorLoading.value,
        publish: publishInActivator,
      },
      {
        key: 'ewizard',
        label: t('author.publishInEwizard.title'),
        icon: 'act',
        color: '#F242FF',
        hasAccess: props.connectorAccess.ewizard,
        isBusy: isEwizardPublishing.value,
        publish: publishInEwizard,
      },
    ]);

    const availableConnectors = computed(() => connectors.value.filter((connector) => connector.hasAccess));
    const primaryConnector = computed(() => availableConnectors.value[0]);
    const menuConnectors = computed(() => availableConnectors.value.slice(1));

    return {
      t,
      isDownloadInProgress,
      primaryConnector,
      menuConnectors,
      isActivatorLoading,
      isActivatorError,
      activatorProgress,
      isEwizardPublishing,
      isEwizardError,
      ewizardProgress,
    };
  },
};
</script>

<style lang="scss" scoped>
.dropdown-button {
  :deep(.v-btn__content) {
    padding: 0 $bb-spacing-2 0 $bb-spacing-3;
    height: 100%;
    border-top-left-radius: 24px;
    border-bottom-left-radius: 24px;
    color: $bb-neutral-1;
    gap: $bb-spacing-1;

    i {
      color: #f242ff;
    }

    &:hover {
      background-color: $bb-neutral-5 !important;
    }
  }

  :deep(.v-btn__append) {
    border-left: 1px solid $bb-neutral-4;
    height: 100%;
    margin: 0 !important;
    border-top-right-radius: 24px;
    border-bottom-right-radius: 24px;

    .bb-icon {
      padding: 0 $bb-spacing-3 0 $bb-spacing-2;
      background-color: inherit !important;
      color: $bb-neutral-1;
    }

    &:hover,
    &:active,
    &:focus {
      background-color: $bb-neutral-5 !important;
    }
  }
}

:deep(.v-list-item__content) {
  display: flex !important;
  align-items: center !important;
  gap: $bb-spacing-1 !important;
}
</style>
